import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import moment from "moment-timezone";
import { useNavigate } from "react-router-dom";

import breakpoints from "_constants/breakpoints";

import { ReactComponent as ExternalLinkSVG } from "assets/img/ExternalLink.svg";

const Container = styled.div`
  width: 100%;
  max-width: 1364px;
  margin-bottom: 48px;
  border-radius: 0px;
  overflow: hidden;
  border: 1px solid #ecedf1;
  box-shadow: 0px 3px 25px rgba(168, 168, 168, 0.3);
`;

const Title = styled.h1`
  margin-bottom: 20px;
  font-family: "Montserrat";
  font-style: normal;
  font-weight: bold;
  font-size: 24px;
  line-height: 40px;
  text-align: center;
  color: #7b848d;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  min-height: 56px;
  padding: 0 24px;
  background-color: #f37217;

  @media (max-width: ${breakpoints.laptop}) {
    display: none;
  }
`;

const HeaderCell = styled.div`
  flex: ${({ flex }) => flex || 1};
  font-style: normal;
  font-weight: bold;
  font-size: 16px;
  color: #ffffff;
`;

const Body = styled.div`
  background-color: #ffffff;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  min-height: 72px;
  padding: 16px 24px;
  cursor: pointer;

  &:not(:last-child) {
    border-bottom: 1px solid #ecedf1;
  }

  &:hover {
    background-color: #f8f9fa;
  }

  @media (max-width: ${breakpoints.laptop}) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const Cell = styled.div`
  flex: ${({ flex }) => flex || 1};
  font-size: 16px;
  color: #394149;

  @media (max-width: ${breakpoints.laptop}) {
    width: 100%;
  }
`;

const MobileLabel = styled.span`
  display: none;
  font-weight: bold;
  color: #7b848d;
  margin-right: 6px;

  @media (max-width: ${breakpoints.laptop}) {
    display: inline;
  }
`;

const NameLink = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: bold;
  color: #394149;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    color: #03589c;
    text-decoration: underline;
  }
`;

const ProductName = styled.div`
  margin-top: 4px;
  margin-left: 22px;
  font-size: 13px;
  color: #a5aaaf;

  @media (max-width: ${breakpoints.laptop}) {
    margin-left: 0;
  }
`;

const Status = styled.span`
  font-weight: bold;
  color: ${({ resolved }) => (resolved ? "#44cdc8" : "#e03131")};
`;

const Date = styled.span`
  color: #394149;
`;

const EmptyMessage = styled.div`
  padding: 24px;
  text-align: center;
  color: #7b848d;
`;

function RequiredActionRow({ title, link, productName, timestamp, resolvedTimestamp }) {
  const navigate = useNavigate();
  return (
    <Row onClick={() => navigate(link)}>
      <Cell flex={3}>
        <NameLink>
          <ExternalLinkSVG />
          {title}
        </NameLink>
        {productName && <ProductName>For Product: {productName}</ProductName>}
      </Cell>
      <Cell flex={1}>
        <MobileLabel>Status:</MobileLabel>
        <Status resolved={Boolean(resolvedTimestamp)}>{resolvedTimestamp ? "Resolved" : "Unresolved"}</Status>
      </Cell>
      <Cell flex={1}>
        <MobileLabel>Date:</MobileLabel>
        <Date>{timestamp ? moment(timestamp).format("MM/DD/YYYY") : "–"}</Date>
      </Cell>
    </Row>
  );
}

RequiredActionRow.propTypes = {
  title: PropTypes.string,
  link: PropTypes.string,
  productName: PropTypes.string,
  timestamp: PropTypes.string,
  resolvedTimestamp: PropTypes.string,
};

function RequiredActions({ actions }) {
  return (
    <>
      <Title>Required Actions</Title>
      <Container>
        <HeaderRow>
          <HeaderCell flex={3}>Name</HeaderCell>
          <HeaderCell flex={1}>Status</HeaderCell>
          <HeaderCell flex={1}>Date</HeaderCell>
        </HeaderRow>
        <Body>
          {actions.length === 0 ? (
            <EmptyMessage>No required actions</EmptyMessage>
          ) : (
            actions.map((action) => (
              <RequiredActionRow
                key={action.id}
                title={action.title}
                link={action.link}
                productName={action.product?.code?.customerName}
                resolvedTimestamp={action.resolvedTimestamp}
              />
            ))
          )}
        </Body>
      </Container>
    </>
  );
}

RequiredActions.propTypes = {
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string,
      link: PropTypes.string,
      timestamp: PropTypes.string,
      resolvedTimestamp: PropTypes.string,
      product: PropTypes.shape({
        code: PropTypes.shape({
          customerName: PropTypes.string,
        }),
      }),
    })
  ),
};

RequiredActions.defaultProps = {
  actions: [],
};

export default RequiredActions;
